import { AppPageWrapper } from "@shared/ui";
import PerksSkeleton from "./PerksSkeleton";
import { TranslateLink } from "@shared/ui";
import usePerks from "@features/clinics/hooks/usePerks";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./Perks.scss";
import {
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  Collapsible,
  InlineStack,
  Loading,
  Page,
  Text,
  Popover,
  OptionList,
} from "@medentic/polaris-react";

const Perks = () => {
  const { t } = useTranslation();
  const { perks, categories, companies, isLoading, isFetching } = usePerks();
  const [selectedCategory, setSelectedCategory] = useState<string[]>([""]);
  const [selectedCompany, setSelectedCompany] = useState<string[]>([""]);
  const [open, setOpen] = useState<string | null>(null);
  const [popoverActiveCategory, setPopoverActiveCategory] = useState(false);
  const [popoverActiveCompany, setPopoverActiveCompany] = useState(false);

  const filteredPerks = useMemo(() => {
    let perksFiltered = perks;

    if (selectedCategory.length > 0 && selectedCategory[0] !== "") {
      perksFiltered = perksFiltered.filter(
        (perk) => perk.categoryId === selectedCategory[0]
      );
    }

    if (selectedCompany.length > 0 && selectedCompany[0] !== "") {
      perksFiltered = perksFiltered.filter(
        (perk) => perk.companyId === selectedCompany[0]
      );
    }

    return perksFiltered;
  }, [perks, selectedCategory, selectedCompany]);

  const handleCategoryChange = (selected: string[]) => {
    setSelectedCategory(selected);
    setPopoverActiveCategory(false);
  };

  const handleCompanyChange = (selected: string[]) => {
    setSelectedCompany(selected);
    setPopoverActiveCompany(false);
  };

  const toggleCollapsible = (perkId: string) => {
    setOpen((prevOpen) => (prevOpen === perkId ? null : perkId));
  };

  const togglePopoverActiveCategory = useCallback(
    () => setPopoverActiveCategory((popoverActive) => !popoverActive),
    []
  );

  const togglePopoverActiveCompany = useCallback(
    () => setPopoverActiveCompany((popoverActive) => !popoverActive),
    []
  );

  const getCategoryTitle = (categoryId: string) => {
    const category = categories.find((category) => category.id === categoryId);
    return category ? category.title : "";
  };

  const getCompanyLogo = (companyId: string) => {
    const company = companies.find((company) => company.id === companyId);
    return company ? company.logoSquare : "";
  };

  return (
    <AppPageWrapper>
      {isLoading ? (
        <PerksSkeleton />
      ) : (
        <Page title={t("perks.title")} subtitle={t("perks.description")}>
          <BlockStack gap={"400"}>
            <InlineStack gap={"200"}>
              <Popover
                active={popoverActiveCategory}
                activator={
                  <Button onClick={togglePopoverActiveCategory} disclosure>
                    {t("perks.categories")}
                  </Button>
                }
                onClose={togglePopoverActiveCategory}
              >
                <OptionList
                  onChange={handleCategoryChange}
                  options={[
                    { value: "", label: t("perks.all_categories") },
                    ...categories.map((category) => ({
                      value: category.id,
                      label: category.title,
                      active: selectedCategory.includes(category.id),
                    })),
                  ]}
                  selected={selectedCategory}
                />
              </Popover>

              <Popover
                active={popoverActiveCompany}
                activator={
                  <Button onClick={togglePopoverActiveCompany} disclosure>
                    {t("perks.companies")}
                  </Button>
                }
                onClose={togglePopoverActiveCompany}
              >
                <OptionList
                  onChange={handleCompanyChange}
                  options={[
                    { value: "", label: t("perks.all_companies") },
                    ...companies.map((company) => ({
                      value: company.id,
                      label: company.name,
                      active: selectedCompany.includes(company.id),
                    })),
                  ]}
                  selected={selectedCompany}
                />
              </Popover>
            </InlineStack>

            <div className="perks-grid">
              {filteredPerks?.map((perk) => (
                <Card
                  roundedAbove="sm"
                  key={perk.link}
                  background={
                    perk.featured ? "bg-surface-info-active" : undefined
                  }
                >
                  <InlineStack
                    align="space-between"
                    blockAlign="start"
                    wrap={false}
                  >
                    <InlineStack gap={"400"} wrap={false}>
                      <img
                        alt={t("common.logoAlt")}
                        src={getCompanyLogo(perk.companyId)}
                        width="40px"
                        height="40px"
                        style={{ borderRadius: "7px" }}
                      />
                      <BlockStack gap="100">
                        <InlineStack gap="200">
                          {perk.showAsNew && (
                            <Badge tone="success">{t("perks.badge.new")}</Badge>
                          )}
                          <Text
                            variant="bodyMd"
                            as="span"
                            fontWeight="semibold"
                          >
                            {perk.title}
                          </Text>
                        </InlineStack>
                        <Text variant="bodyMd" as="span" tone="subdued">
                          {getCategoryTitle(perk.categoryId)}
                        </Text>
                      </BlockStack>
                    </InlineStack>
                  </InlineStack>
                  <Box paddingBlockEnd="400" paddingBlockStart="500">
                    <TranslateLink text={perk.description}></TranslateLink>
                  </Box>
                  {perk.howTo && (
                    <Button
                      variant="plain"
                      disclosure={open === perk.link ? "up" : "down"}
                      onClick={() => toggleCollapsible(perk.link)}
                    >
                      {t("perks.how_to_use")}
                    </Button>
                  )}
                  <Box paddingBlockStart="200">
                    <Collapsible
                      open={open === perk.link}
                      id={`collapsible-${perk.link}`}
                      transition={{
                        duration: "500ms",
                        timingFunction: "ease-in-out",
                      }}
                      expandOnPrint
                    >
                      <TranslateLink text={perk.howTo}></TranslateLink>

                      <Box paddingBlockStart={"400"} width="100%">
                        <InlineStack align="end">
                          <Button
                            external
                            variant="primary"
                            target="_blank"
                            url={perk.link}
                          >
                            {t("perks.action")}
                          </Button>
                        </InlineStack>
                      </Box>
                    </Collapsible>
                  </Box>
                </Card>
              ))}
            </div>
          </BlockStack>
          <br />
        </Page>
      )}
      {isFetching && <Loading />}
    </AppPageWrapper>
  );
};

export default Perks;
